import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { TrendingUp, TrendingDown, Minus, ArrowRight, LineChart as LineChartIcon } from "lucide-react";

const METRICS = [
  { id: "reussite_appels", label: "Réussite appels", color: "#EE722E" },
  { id: "spots_decroches", label: "Spots décrochés", color: "#2F6FED" },
  { id: "rebooking", label: "Rebooking", color: "#1F9D55" },
];

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

export function TrendsSummaryWidget() {
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/trends", { params: { indicator: "multi", granularity: "semaine", dimension: "global" } });
        if (alive) setRes(data);
      } catch {
        if (alive) setRes(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="home-tendances-widget">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--surface-2)] text-[var(--shariti-orange)]"><LineChartIcon className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-bold text-ink">Résumé Tendances</div>
            <div className="text-[11px] text-muted-ink">Taux de réussite — 12 dernières semaines</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/tendances")} data-testid="home-tendances-open" className="h-8 gap-1.5 text-[12px]">
          Ouvrir <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {loading
          ? METRICS.map((m) => <Skeleton key={m.id} className="h-[92px] rounded-xl" />)
          : METRICS.map((m) => {
            const kpi = res?.kpis?.[m.id] || {};
            const cur = kpi.current;
            const delta = kpi.delta;
            const hasDelta = delta !== null && delta !== undefined;
            const dir = hasDelta ? (delta > 0 ? "up" : delta < 0 ? "down" : "flat") : "flat";
            const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
            const deltaCls = dir === "up" ? "text-[#0F5A2F]" : dir === "down" ? "text-[#9B2226]" : "text-muted-ink";
            const spark = (res?.data || []).map((r) => ({ x: r.key, y: r[m.id] }));
            return (
              <div key={m.id} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`home-tendances-tile-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">{m.label}</span>
                  <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
                </div>
                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span className="text-lg font-extrabold tabular-nums text-ink">{fmtPct(cur)}</span>
                  <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${deltaCls}`}>
                    <Icon className="h-3 w-3" />{hasDelta ? `${delta > 0 ? "+" : ""}${delta}` : ""}
                  </span>
                </div>
                <div className="mt-1 h-[44px] w-full" data-testid={`home-tendances-spark-${m.id.replace(/_/g, "-")}`}>
                  <ResponsiveContainer width="100%" height={44} minWidth={0} debounce={1}>
                    <LineChart data={spark} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                      <YAxis domain={[0, 100]} hide />
                      <Line type="monotone" dataKey="y" stroke={m.color} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
